/**
 * The browser's WebMCP API (decision 0123; W3C Web Machine Learning CG Draft Report, 16 Oct 2026, and Chrome's
 * imperative API). Minimal local types, no dependency: only what Minga uses. `document.modelContext` is the spec's
 * place; older Chrome builds expose `navigator.modelContext`. Secure contexts only (the attribute is absent elsewhere).
 */

/** Spec `ToolAnnotations`: hints for the agent and the browser (all default to false). */
export interface ModelContextToolAnnotations {
  readonly readOnlyHint?: boolean
  readonly consequentialHint?: boolean
  readonly untrustedContentHint?: boolean
  readonly debugging?: boolean
}

/** Spec `ModelContextTool`: name 1–128 of `[A-Za-z0-9_.-]`; `execute` resolves to any JSON-serialisable value. */
export interface ModelContextTool {
  readonly name: string
  readonly title?: string
  readonly description: string
  readonly inputSchema?: object
  readonly annotations?: ModelContextToolAnnotations
  readonly execute: (
    input: Record<string, unknown>,
    options: { readonly signal: AbortSignal },
  ) => Promise<unknown>
}

/**
 * Spec `ModelContextRegisterToolOptions`: aborting `signal` unregisters the tool. `exposedTo` (other origins) is never
 * used: Minga's tools stay same-origin.
 */
export interface ModelContextRegisterToolOptions {
  readonly signal?: AbortSignal
}

export interface ModelContext {
  /** The spec's returns a promise; early builds returned a registration with `unregister()`, handled too. */
  registerTool(tool: ModelContextTool, options?: ModelContextRegisterToolOptions): unknown
}

const isModelContext = (u: unknown): u is ModelContext =>
  typeof u === "object" && u !== null && typeof (u as { registerTool?: unknown }).registerTool === "function"

/** The page's model context: `document.modelContext`, else `navigator.modelContext` (older builds), else null. */
export const findModelContext = (
  doc: unknown = globalThis.document,
  nav: unknown = globalThis.navigator,
): ModelContext | null => {
  const fromDoc = (doc as { modelContext?: unknown } | undefined)?.modelContext
  if (isModelContext(fromDoc)) return fromDoc
  const fromNav = (nav as { modelContext?: unknown } | undefined)?.modelContext
  return isModelContext(fromNav) ? fromNav : null
}

/**
 * Register `tool` until `signal` aborts. A registration object with `unregister()` (early builds that ignored the
 * signal) is unregistered on abort as well. Resolves false when the browser refused the tool.
 */
export const registerUntil = async (
  context: ModelContext,
  tool: ModelContextTool,
  signal: AbortSignal,
): Promise<boolean> => {
  if (signal.aborted) return false
  try {
    const out = await context.registerTool(tool, { signal })
    const unregister = (out as { unregister?: unknown } | null | undefined)?.unregister
    if (typeof unregister === "function") {
      const run = () => {
        try {
          unregister.call(out)
        } catch {}
      }
      if (signal.aborted) run()
      else signal.addEventListener("abort", run, { once: true })
    }
    return true
  } catch (e) {
    console.warn(`[webmcp] ${tool.name} not registered: ${e instanceof Error ? e.message : String(e)}`)
    return false
  }
}
